import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Eye, ImagePlus, Loader2, Save, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fileToLogo } from "../lib/image";
import { fmtFull, parseNum, rp } from "../lib/format";
import { PageWrap } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const STATUS = { pending: ["Menunggu", "bg-warning/15 text-warning"], approved: ["Disetujui", "bg-success/15 text-success"], rejected: ["Ditolak", "bg-destructive/15 text-destructive"] };
const FILTERS = [["pending", "Menunggu"], ["approved", "Disetujui"], ["rejected", "Ditolak"], ["", "Semua"]];

const Requests = () => {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/licenses", { params: { status: filter } }).then(({ data }) => setRows(data)), [filter]);
  useEffect(() => { setRows(null); load(); }, [load]);
  const act = async (r, kind) => {
    const reason = kind === "reject" ? window.prompt("Alasan penolakan:", "Nominal/bukti tidak sesuai") : "";
    if (reason === null) return;
    if (kind === "revoke" && !window.confirm(`Cabut lisensi ${r.store_name}?`)) return;
    setBusy(r.id + kind);
    try {
      await api.post(`/admin/licenses/${r.id}/${kind}`, kind === "reject" ? { reason } : {});
      toast.success(kind === "approve" ? "Disetujui — owner diberi notifikasi" : "Status diperbarui");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const showProof = async (r) => setProof({ ...r, img: (await api.get(`/admin/licenses/${r.id}/proof`)).data.proof });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <Button key={k || "all"} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`sa-filter-${k || "all"}`}>{l}</Button>
        ))}
      </div>
      {rows === null ? <Loader2 className="h-5 w-5 animate-spin" /> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="sa-empty">Tidak ada pengajuan.</p>
      ) : rows.map((r) => (
        <div key={r.id} className="rounded-xl border p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`sa-row-${r.id}`}>
          <div className="flex-1 min-w-0 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{r.store_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS[r.status]?.[1]}`}>{STATUS[r.status]?.[0]}</span>
            </div>
            <div className="text-muted-foreground break-all">{r.owner_name} · {r.owner_email} {r.phone && `· ${r.phone}`}</div>
            <div className="text-muted-foreground">Pengirim: {r.sender_name} · {rp(r.amount)} · {fmtFull(r.submitted_at)}</div>
            {r.note && <div className="text-muted-foreground">Catatan: {r.note}</div>}
            {r.reject_reason && <div className="text-destructive">Alasan: {r.reject_reason}</div>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => showProof(r)} data-testid={`sa-proof-${r.id}`}><Eye className="h-4 w-4 mr-1" />Bukti</Button>
            {r.status !== "approved" && <Button size="sm" onClick={() => act(r, "approve")} disabled={!!busy} data-testid={`sa-approve-${r.id}`}><Check className="h-4 w-4 mr-1" />Setujui</Button>}
            {r.status === "pending" && <Button size="sm" variant="destructive" onClick={() => act(r, "reject")} disabled={!!busy} data-testid={`sa-reject-${r.id}`}><X className="h-4 w-4 mr-1" />Tolak</Button>}
            {r.status === "approved" && <Button size="sm" variant="ghost" onClick={() => act(r, "revoke")} disabled={!!busy} data-testid={`sa-revoke-${r.id}`}>Cabut</Button>}
          </div>
        </div>
      ))}
      <Dialog open={!!proof} onOpenChange={(o) => !o && setProof(null)}>
        <DialogContent className="max-w-2xl">
          <DialogTitle>Bukti pembayaran · {proof?.store_name}</DialogTitle>
          {proof?.img && <img src={proof.img} alt="Bukti" className="max-h-[70vh] w-full object-contain rounded-lg" data-testid="sa-proof-image" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PriceSettings = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/license-settings").then(({ data }) => setF({ ...data, price: String(data.price || "") })); }, []);
  if (!f) return <Loader2 className="h-5 w-5 animate-spin" />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/license-settings", { ...f, price: parseNum(f.price) });
      toast.success("Harga & info pembayaran disimpan");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 max-w-3xl" data-testid="sa-price-form">
      <div className="space-y-1.5"><Label>Harga lisensi (Rp)</Label><Input value={f.price} onChange={set("price")} data-testid="sa-price-input" /></div>
      <div className="space-y-1.5"><Label>Keterangan harga</Label><Input value={f.price_label} onChange={set("price_label")} data-testid="sa-price-label-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Rekening tujuan transfer</Label><Textarea rows={4} value={f.bank_info} onChange={set("bank_info")} data-testid="sa-bank-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Instruksi pembayaran</Label><Textarea rows={3} value={f.instructions} onChange={set("instructions")} data-testid="sa-instructions-input" /></div>
      <div className="space-y-2 sm:col-span-2">
        <Label>Gambar QRIS (opsional)</Label>
        <div className="flex items-center gap-3">
          {f.qris_image && <img src={f.qris_image} alt="QRIS" className="h-28 rounded-lg border bg-white p-1" />}
          <Button type="button" variant="outline" asChild><label className="cursor-pointer"><ImagePlus className="h-4 w-4 mr-2" />Unggah QRIS
            <input type="file" accept="image/*" hidden onChange={async (e) => e.target.files[0] && setF({ ...f, qris_image: await fileToLogo(e.target.files[0], 900) })} data-testid="sa-qris-input" /></label></Button>
          {f.qris_image && <Button type="button" variant="ghost" onClick={() => setF({ ...f, qris_image: "" })}>Hapus</Button>}
        </div>
      </div>
      <div className="sm:col-span-2"><Button type="submit" disabled={busy} data-testid="sa-price-save-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Simpan</Button></div>
    </form>
  );
};

export default function SuperAdmin() {
  return (
    <PageWrap>
      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests" data-testid="sa-tab-requests">Pengajuan Lisensi</TabsTrigger>
          <TabsTrigger value="price" data-testid="sa-tab-price">Harga & Rekening</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="mt-6"><Requests /></TabsContent>
        <TabsContent value="price" className="mt-6"><PriceSettings /></TabsContent>
      </Tabs>
    </PageWrap>
  );
}
